// FormsSidebar — collapsible left navigation for the Forms area.
//
// Expanded: labelled nav items. Collapsed: icon-only rail with tooltips.
// "Forms" navigates to /forms (active across the forms browsing area). The
// "Form Setup" group holds setup pages: Classification Sets is live; Map Layers
// and Choice Lists are stubbed with a "coming soon" snackbar until their routes
// exist.

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { alpha, Box, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSnackbar } from "notistack";

const EXPANDED_WIDTH = 248;
const COLLAPSED_WIDTH = 72;
const STORAGE_KEY = "cm-forms-sidebar-collapsed";

interface NavItem {
  label: string;
  icon: React.ReactElement;
  /** Navigable route. */
  path?: string;
  /** Custom active matcher (defaults to exact `path` match). */
  match?: (pathname: string) => boolean;
  /** Not-yet-built page — shows a "coming soon" snackbar. */
  comingSoon?: boolean;
}

const FORMS_ITEM: NavItem = {
  label: "Forms",
  icon: <DescriptionOutlinedIcon fontSize='small' />,
  path: "/forms",
  // Active across the forms browsing area, but not the setup pages.
  match: (p) => p.startsWith("/forms") && !p.startsWith("/forms/classification-sets")
};

const SETUP_ITEMS: NavItem[] = [
  { label: "Map Layers", icon: <LayersOutlinedIcon fontSize='small' />, comingSoon: true },
  { label: "Choice Lists", icon: <FormatListBulletedOutlinedIcon fontSize='small' />, comingSoon: true },
  {
    label: "Classification Sets",
    icon: <AccountTreeOutlinedIcon fontSize='small' />,
    path: "/forms/classification-sets",
    match: (p) => p.startsWith("/forms/classification-sets")
  }
];

const FormsSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });

  const isActive = (item: NavItem) => {
    if (!item.path) return false;
    return item.match ? item.match(location.pathname) : location.pathname === item.path;
  };

  const handleClick = (item: NavItem) => {
    if (item.path) navigate(item.path);
    else if (item.comingSoon) enqueueSnackbar(`${item.label} — coming soon`, { variant: "info" });
  };

  const renderItem = (item: NavItem) => {
    const active = isActive(item);
    const button = (
      <ListItemButton
        onClick={() => handleClick(item)}
        selected={active}
        sx={{
          borderRadius: 2,
          mx: collapsed ? "10px" : "12px",
          my: "2px",
          minHeight: 44,
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 0 : "12px",
          color: "text.primary",
          transition: "background-color .15s ease, color .15s ease",
          "&.Mui-selected": {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            color: "primary.main",
            "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.18) }
          }
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : "12px",
            justifyContent: "center",
            color: active ? "primary.main" : "text.secondary"
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500, noWrap: true }} />
        )}
      </ListItemButton>
    );

    return collapsed ? (
      <Tooltip key={item.label} title={item.label} placement='right' arrow>
        <Box>{button}</Box>
      </Tooltip>
    ) : (
      <Box key={item.label}>{button}</Box>
    );
  };

  return (
    <Box
      component='nav'
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        height: "100%",
        borderRight: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: (t) => t.transitions.create("width", { duration: t.transitions.duration.shorter })
      }}
    >
      {/* Header / collapse toggle */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : "16px",
          height: 56,
          flexShrink: 0
        }}
      >
        {!collapsed && (
          <Typography variant='overline' sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: 0.6 }}>
            Menu
          </Typography>
        )}
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement='right' arrow>
          <IconButton size='small' onClick={toggle}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      {/* Primary */}
      <List sx={{ py: "8px" }} disablePadding>
        {renderItem(FORMS_ITEM)}
      </List>

      {/* Separator + Form Setup group */}
      <Divider sx={{ mx: collapsed ? "10px" : "16px" }} />
      {collapsed ? (
        <Box sx={{ height: "12px" }} />
      ) : (
        <Typography
          variant='caption'
          sx={{ px: "20px", pt: "14px", pb: "4px", display: "block", color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}
        >
          FORM SETUP
        </Typography>
      )}
      <List sx={{ py: collapsed ? "4px" : "2px" }} disablePadding>
        {SETUP_ITEMS.map(renderItem)}
      </List>
    </Box>
  );
};

export default FormsSidebar;
